import { useId } from 'react';
import * as styles from './index.css';

/** Side of the square viewBox the design asset was drawn in. */
const GRID_EXTENT = 382;

const VIGNETTE_RADIUS = GRID_EXTENT / 2;

/** Dashed grid line offsets from the design asset, in viewBox units. */
const COLUMN_OFFSETS = [
  15.7969, 37.7539, 59.7109, 81.6602, 103.617, 125.566, 147.523, 169.477, 191.434, 213.387, 235.34, 257.297, 279.246,
  301.203, 323.152, 345.109, 367.066,
];

const ROW_OFFSETS = [
  16.375, 38.3281, 60.2812, 82.2363, 104.191, 126.145, 148.1, 170.053, 192.008, 213.961, 235.916, 257.869, 279.822,
  301.777, 323.732, 345.686, 367.639,
];

/**
 * Dashed-grid vignette behind the Agent welcome suggestions.
 *
 * Inlined rather than loaded through `<img>` so the strokes can inherit a theme token.
 * The asset it replaced baked in a light-mode-only stroke colour and faded its edges
 * with a white overlay that was only invisible under `mix-blend-mode: multiply` — and
 * that blend isolated itself whenever an ancestor animated opacity, flashing the raw
 * white vignette over the dark surface for the length of the entrance. The taper is a
 * real alpha mask here, so nothing depends on the blend mode.
 */
export function EmptyStateTexture() {
  const id = useId().replace(/:/g, '');
  const maskId = `copilot-empty-state-texture-mask-${id}`;
  const vignetteId = `copilot-empty-state-texture-vignette-${id}`;

  return (
    <svg
      className={styles.texture}
      width={GRID_EXTENT}
      height={GRID_EXTENT}
      viewBox={`0 0 ${GRID_EXTENT} ${GRID_EXTENT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <radialGradient
          id={vignetteId}
          cx={VIGNETTE_RADIUS}
          cy={VIGNETTE_RADIUS}
          r={VIGNETTE_RADIUS}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </radialGradient>
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width={GRID_EXTENT} height={GRID_EXTENT}>
          <circle cx={VIGNETTE_RADIUS} cy={VIGNETTE_RADIUS} r={VIGNETTE_RADIUS} fill={`url(#${vignetteId})`} />
        </mask>
      </defs>
      <g
        mask={`url(#${maskId})`}
        stroke="currentColor"
        strokeWidth="1.07707"
        strokeLinecap="round"
        strokeDasharray="5.46 5.46"
      >
        {COLUMN_OFFSETS.map((x) => (
          <line key={`column-${x}`} x1={x} y1={0} x2={x} y2={GRID_EXTENT} />
        ))}
        {ROW_OFFSETS.map((y) => (
          <line key={`row-${y}`} x1={0} y1={y} x2={GRID_EXTENT} y2={y} />
        ))}
      </g>
    </svg>
  );
}
